import { CropWheatIcon } from '@protector/icons';
import { Icon, Tooltip } from 'antdV3';
import { COLOR_OPTIONS } from 'config/style';
import STTypo, { STTypoModels } from 'core/shared/typo';
import type { Feature, Point } from 'geojson';
import type { RegionalOverviewProperties } from 'querys/regional-overview/regional-overview.query.models';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import RegionalOverviewMycotoxinFieldLevelType from '../../../components/regional-overview-mycotoxin-field-level-type/regional-overview-mycotoxin-field-level-type';
import { regionalMycotoxinLevels, VariableTypes } from '../../regional-overview-mycotoxin.models';
import { setCorrectValues } from '../../regional-overview-mycotoxin.utils';
import './regional-overview-mycotoxin-field-container.less';

interface RegionalOverviewMycotoxinFieldContainerProps {
  fieldInfo: Feature<Point, RegionalOverviewProperties>;
  currentUnitSystem: string;
  handleClosePopup: () => void;
}

const container = 'regional-overview-mycotoxin-field-container';
const translatePrefix = 'pages.regional_overview.regional_mycotoxin.field_card_info';

const RegionalOverviewMycotoxinFieldContainer = ({
  fieldInfo,
  currentUnitSystem,
  handleClosePopup
}: RegionalOverviewMycotoxinFieldContainerProps): JSX.Element => {
  const [t] = useTranslation();

  const { agronomic_class, blooming_date, field_size, planting_date, risk, risk_value, previously_crop, tillage, variety, property_name } =
    fieldInfo.properties;

  const riskLevel = regionalMycotoxinLevels.find(r => r.value === risk);
  const hasBigName = fieldInfo.properties.name.length > 8;

  const fieldSize = useMemo(() => {
    return setCorrectValues(VariableTypes.FIELD_SIZE, field_size, currentUnitSystem);
  }, [currentUnitSystem, field_size]);

  return (
    <div data-testid={`${container}`} className={container}>
      <div className={`${container}__wrapper-header level_risk__${riskLevel?.type}`}>
        <div
          data-testid={`${container}-crop-container`}
          className={`${container}__wrapper-header__crop-container level_risk__${riskLevel?.type}`}>
          <CropWheatIcon />
        </div>
        <div
          data-testid={`${container}-info-container`}
          className={`${container}__wrapper-header__info-container ${hasBigName && 'has-big-name'}`}>
          <STTypo
            data-testid={`${container}-info-container-property-name`}
            type={STTypoModels.TypeOptions.P2}
            color={COLOR_OPTIONS.NEUTRAL_80}>
            {setCorrectValues(VariableTypes.PROPERTY_NAME, property_name)}
          </STTypo>

          <Tooltip data-testid={`${container}-info-container-tooltip`} placement='bottomLeft' title={fieldInfo.properties.name}>
            <STTypo type={STTypoModels.TypeOptions.H4} color={COLOR_OPTIONS.NEUTRAL_80} fontWeight={500}>
              {fieldInfo.properties.name}
            </STTypo>
          </Tooltip>
        </div>
        <div data-testid={`${container}-level-container`} className={`${container}__wrapper-header__level-container`}>
          <RegionalOverviewMycotoxinFieldLevelType fieldRiskLevel={riskLevel} />
          <STTypo>{String(fieldSize)}</STTypo>
        </div>
        <div data-testid={`${container}-close-button`} className={`${container}__wrapper-header__close-button`} onClick={handleClosePopup}>
          <Icon type='close' style={{ fontSize: 18, color: COLOR_OPTIONS.SECONDARY }} />
        </div>
      </div>
      <div data-testid={`${container}-wrapper-body`} className={`${container}__wrapper-body`}>
        <div data-testid={`${container}-level-acceptance-container`} className={`${container}__wrapper-body__level-acceptance`}>
          <STTypo type={STTypoModels.TypeOptions.P2} color={COLOR_OPTIONS.NEUTRAL_60}>
            {t(`${translatePrefix}.level_acceptance_title`)}
          </STTypo>
          <STTypo type={STTypoModels.TypeOptions.H5} color={COLOR_OPTIONS.NEUTRAL_80} fontWeight={500}>
            {t(`${translatePrefix}.level_value`, {
              value: setCorrectValues(VariableTypes.RISK_VALUE, risk_value)
            })}
          </STTypo>
        </div>
        <div data-testid={`${container}-wrapper-body-info-container`} className={`${container}__wrapper-body__info-container`}>
          <div
            data-testid={`${container}-wrapper-body-info-container-right-side`}
            className={`${container}__wrapper-body__info-container__right-side`}>
            <div
              data-testid={`${container}-wrapper-body-info-container-agronomic_class`}
              className={`${container}__wrapper-body__info-container__items`}>
              <STTypo type={STTypoModels.TypeOptions.P2} color={COLOR_OPTIONS.NEUTRAL_60}>
                {t(`${translatePrefix}.infos.agronomic_class`)}
              </STTypo>
              <STTypo type={STTypoModels.TypeOptions.P2} color={COLOR_OPTIONS.NEUTRAL_80} fontWeight={500}>
                {setCorrectValues(VariableTypes.AGRONOMIC_CLASS, agronomic_class)}
              </STTypo>
            </div>
            <div
              data-testid={`${container}-wrapper-body-info-container-variety`}
              className={`${container}__wrapper-body__info-container__items`}>
              <STTypo type={STTypoModels.TypeOptions.P2} color={COLOR_OPTIONS.NEUTRAL_60}>
                {t(`${translatePrefix}.infos.variety`)}
              </STTypo>
              <STTypo type={STTypoModels.TypeOptions.P2} color={COLOR_OPTIONS.NEUTRAL_80} fontWeight={500}>
                {setCorrectValues(VariableTypes.VARIETY, variety)}
              </STTypo>
            </div>
            <div
              data-testid={`${container}-wrapper-body-info-container-planting_date`}
              className={`${container}__wrapper-body__info-container__items`}>
              <STTypo type={STTypoModels.TypeOptions.P2} color={COLOR_OPTIONS.NEUTRAL_60}>
                {t(`${translatePrefix}.infos.planting_date`)}
              </STTypo>
              <STTypo type={STTypoModels.TypeOptions.P2} color={COLOR_OPTIONS.NEUTRAL_80} fontWeight={500}>
                {setCorrectValues(VariableTypes.PLANTING_DATE, planting_date)}
              </STTypo>
            </div>
          </div>
          <div
            data-testid={`${container}-wrapper-body-info-container-left-side`}
            className={`${container}__wrapper-body__info-container__left-side`}>
            <div
              data-testid={`${container}-wrapper-body-info-container-criteria_tillage`}
              className={`${container}__wrapper-body__info-container__items`}>
              <STTypo type={STTypoModels.TypeOptions.P2} color={COLOR_OPTIONS.NEUTRAL_60}>
                {t(`${translatePrefix}.infos.criteria_tillage`)}
              </STTypo>
              <STTypo type={STTypoModels.TypeOptions.P2} color={COLOR_OPTIONS.NEUTRAL_80} fontWeight={500}>
                {setCorrectValues(VariableTypes.TILLAGE, tillage)}
              </STTypo>
            </div>
            <div
              data-testid={`${container}-wrapper-body-info-container-previous_crop`}
              className={`${container}__wrapper-body__info-container__items`}>
              <STTypo type={STTypoModels.TypeOptions.P2} color={COLOR_OPTIONS.NEUTRAL_60}>
                {t(`${translatePrefix}.infos.previous_crop`)}
              </STTypo>
              <STTypo type={STTypoModels.TypeOptions.P2} color={COLOR_OPTIONS.NEUTRAL_80} fontWeight={500}>
                {setCorrectValues(VariableTypes.PREVIOUSLY_CROP, previously_crop)}
              </STTypo>
            </div>
            <div
              data-testid={`${container}-wrapper-body-info-container-blooming_date`}
              className={`${container}__wrapper-body__info-container__items`}>
              <STTypo type={STTypoModels.TypeOptions.P2} color={COLOR_OPTIONS.NEUTRAL_60}>
                {t(`${translatePrefix}.infos.blooming_date`)}
              </STTypo>
              <STTypo type={STTypoModels.TypeOptions.P2} color={COLOR_OPTIONS.NEUTRAL_80} fontWeight={500}>
                {setCorrectValues(VariableTypes.BLOOMING_DATE, blooming_date)}
              </STTypo>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegionalOverviewMycotoxinFieldContainer;
